import { EMPTY_STRING, SPACE } from "../../AppConstants";
import { ErrorStringFromException, isNullOrUndefined } from "../../utils/utils";
import { DocumentStorage } from "../office-js/DocumentStorage";
import { DocumentStorageError, DocumentStorageErrors } from "../office-js/DocumentStorageErrors";
import { HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import oneNoteTranscriptionManager, { GetAssociatedNotebookId, GetAssociatedPageId, GetAssociatedSectionId, GetSectionFromSectionGroup, NavigateToAndFetchAssociatedPage } from "./OneNoteTranscriptionManager";
import { TranscriptionStorageType } from "../interfaces/IStoredTranscription";

const enum ContentUpdateToStorageStatus {
  LinkedNotebookNotFound = 'LinkedNotebookNotFound',
  LinkedSectionNotFound = 'LinkedSectionNotFound',
  LinkedPageNotFound = 'LinkedPageNotFound',
  WriteToLinkedPageFailed = 'WriteToLinkedPageFailed',
  WriteToLinkedPageSuccess = 'WriteToLinkedPageSuccess'
}

interface ContentUpdateRequestResult {
  status: ContentUpdateToStorageStatus,
  statusDescription: string // could be error details or any relevant description of status.
}

export class DocumentStorageOneNote extends DocumentStorage {

 constructor() {
    super(TranscriptionStorageType.DocumentStorage);
  }

  /*InOneNote Win32, Transcription content is coming as Json object during Transcription pane launch
  but during Transcribing, {HostAppSetting.Set} API save the transcription content as string.
  Hence in order to handle both case and avoid failure, JSON.parse is done in case typeof fetched content from HostAppSetting
  is "string".*/
  GetStoredTranscription(): any {
    try {
      let storedTranscription = HostAppSettings.GetSetting(HostAppSettingName.StoredTranscription);
      const storedTranscriptionJson = (typeof storedTranscription == "string") ? JSON.parse(storedTranscription) : storedTranscription;
      return storedTranscriptionJson;
    } catch (e) {
      throw new DocumentStorageError(DocumentStorageErrors.CorruptTranscriptSource, ErrorStringFromException(e));
    }
  }

  async WriteTranscribeContentToClient(content: string): Promise<void> {

    if (oneNoteTranscriptionManager.IsAssociatedPageAvailable()) {

      /* perform update on associated page, if not success in case associated notebookId,
         sectionId or pageId is deleted/moved.*/
      const result: ContentUpdateRequestResult = await this.UpdateContentToAssociatedPage(content);
      await this.handleContentUpdateToStorageResult(result, content);
    }
  }

  async DeleteTranscribeContentFromClient(): Promise<void> {

    if (oneNoteTranscriptionManager.IsAssociatedPageAvailable()) {

      /* perform update on associated page, if not success in case associated notebookId,
         sectionId or pageId is deleted/moved.*/
      const result: ContentUpdateRequestResult = await this.UpdateContentToAssociatedPage();
      await this.handleContentUpdateToStorageResult(result);
    }
  }

  private async handleContentUpdateToStorageResult(result: ContentUpdateRequestResult, content: string = EMPTY_STRING) {
    switch (result.status) {
      case ContentUpdateToStorageStatus.LinkedNotebookNotFound:
      case ContentUpdateToStorageStatus.LinkedSectionNotFound:
      case ContentUpdateToStorageStatus.LinkedPageNotFound:
        // fail case: Linked Page not found, perform complete lookup for page in all Notebooks and all sections
        let isSuccess = await this.PerformCompleteLookupAndUpdate(content);
        if (!isSuccess) {
          if (oneNoteTranscriptionManager.IsAssociatedPageAvailable()) {
            throw new DocumentStorageError(DocumentStorageErrors.DocumentNotFound,
              JSON.stringify(result));
          }
        }
        break;
      case ContentUpdateToStorageStatus.WriteToLinkedPageFailed:
        // Fail case: Linked page found but write to linked page failed.
        if (content.length > 0) {
          throw new DocumentStorageError(DocumentStorageErrors.WriteContentToClientStorageFailed,
            JSON.stringify(result));
        } else {
          throw new DocumentStorageError(DocumentStorageErrors.DeleteContentFromClientStorageFailed,
            JSON.stringify(result));
        }
    }
  }

  /**
  * UpdateContentToAssociatedPage will update the provided content on Associated Page and
  * try to navigate to associated OneNote page.
  * @method UpdateContentToAssociatedPage
  * @param {content} string content to be writen, if no content is provided, already existing content on associated page will be cleaned up.
  * @return {boolean} true on success else failure.
  */
  private async UpdateContentToAssociatedPage(content: string = ""): Promise<ContentUpdateRequestResult> {

    let result: ContentUpdateRequestResult = {
      status: ContentUpdateToStorageStatus.WriteToLinkedPageSuccess,
      statusDescription: "success"
    };

    let linkedPage: OneNote.Page | null = null;
    let errorDescription = "";
    await OneNote.run(async (context) => {

      //navigate to linked page to make storage update faster and fail safe.
      try {
        linkedPage = await NavigateToAndFetchAssociatedPage(context);
      } catch (e) {
        errorDescription = "Navigation to linked page failed during UpdateContentToAssociatedPage call with error " +
          ErrorStringFromException(e);
      }

      if (isNullOrUndefined(linkedPage)) {
        //fetch associated notebook.
        let noteBook = null;
        try {
          noteBook = context.application.notebooks.getItem(GetAssociatedNotebookId());
          await context.sync();
        } catch (e) {
          result.status = ContentUpdateToStorageStatus.LinkedNotebookNotFound;
          result.statusDescription = errorDescription + "Notebook id : " + GetAssociatedNotebookId() + SPACE + ErrorStringFromException(e);
          return result;
        }
        //fetch associated Section.
        let section = null;
        try {
          section = await GetSectionFromSectionGroup(noteBook, context);
          if (isNullOrUndefined(section)) {
            section = noteBook.sections.getItem(GetAssociatedSectionId());
          }
          await context.sync();
        } catch (e) {
          result.status = ContentUpdateToStorageStatus.LinkedSectionNotFound;
          result.statusDescription = errorDescription + "Section id : " + GetAssociatedSectionId() + SPACE + ErrorStringFromException(e);
          return result;
        }
        //fetch associated page from associated section.
        try {
          linkedPage = section!.pages.getItem(GetAssociatedPageId());
          await context.sync();
        } catch (e) {
          result.status = ContentUpdateToStorageStatus.LinkedPageNotFound;
          result.statusDescription = errorDescription + "Page id : " + GetAssociatedPageId() + SPACE + ErrorStringFromException(e);
          return result;
        }
      }

      //update Transcription data to page.
      try {
        const transcription = (<any>linkedPage).transcription;
        transcription.data = content;
        await context.sync();

        //update result as Success.
        return result;
      } catch (e) {
        result.status = ContentUpdateToStorageStatus.WriteToLinkedPageFailed;
        result.statusDescription = errorDescription + ErrorStringFromException(e);
        return result;
      }
    });
    return result;
  }

  private async PerformCompleteLookupAndUpdate(content: string = ""): Promise<boolean> {
    let isSuccess: boolean = false;
    await OneNote.run(async (context) => {

      // lookup for associated page, need to iterate through pages of All session of All notebooks.
      const notebooks = context.application.notebooks;
      notebooks.load('count');
      await context.sync();

      //iterate through notebooks and fetch all sessions for specific notebook.
      for (let noteBookIndex = 0; noteBookIndex < notebooks.count; noteBookIndex++) {
        if (!isSuccess) {
          const noteBook = notebooks.getItem(noteBookIndex);
          const sections = noteBook.sections;
          sections.load('count');
          await context.sync();

          // iterate through sessions and lookup for associated page
          for (let sectionIndex = 0; sectionIndex < sections.count; sectionIndex++) {
            const section = sections.getItem(sectionIndex);

            try {
              let page = section.pages.getItem(GetAssociatedPageId());
              const transcription = (<any>page).transcription;
              transcription.data = content;
              await context.sync();

              // persist notebook Id
              noteBook.load('id');
              await context.sync();
              HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteNotebookId, noteBook.id);

              //persist section Id
              section.load('id');
              await context.sync();
              HostAppSettings.Set(HostAppSettingName.AssociatedOneNoteSectionId, section.id);

              isSuccess = true;
              break;
            } catch (e: any) {
              // page not present in this session, need to iterate through other session
            }
          }
        }
      }
    });
    return isSuccess;
  }
}